import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Typography,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  Checkbox,
  Snackbar
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import OrderService from '../../../services/order.service';

function OrderDetails({ order, open, onClose }) {
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [transactionId, setTransactionId] = useState(order?.payment?.idTransaction || '');
  const [paymentPartner, setPaymentPartner] = useState("FEDAPAY");
  const [refundItems, setRefundItems] = useState([]);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleVerifyPayment = async () => {
    if (!transactionId) {
      setVerificationResult({
        success: false,
        message: "Veuillez entrer l'ID de transaction."
      });
      return;
    }

    setVerifying(true);
    setVerificationResult(null);
    
    try {
      const payload = {
        order_id: order?.id,
        id_transaction: transactionId,
        payment_partner: paymentPartner
      };
      
      const response = await OrderService.verifyPayment(payload);
      setVerificationResult({
        success: response.statut === "success",
        message: response.statut === "success" ? "Vérification effectuée avec succès" : "Échec de la vérification"
      });

    } catch (error) {
      setVerificationResult({
        success: false,
        message: error.message || "Erreur lors de la vérification"
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleRefund = async () => {
    if (refundItems.length === 0) {
      setSnackbar({
        open: true,
        message: "Veuillez sélectionner au moins un article à rembourser",
        severity: 'warning'
      });
      return;
    }

    const payload = {
      order_id: order?.id,
      order_items: refundItems
    };

    try {
      const response = await OrderService.refundOrder(payload);
      setSnackbar({
        open: true,
        message: response.message || "Remboursement effectué avec succès",
        severity: 'success'
      });
      setShowRefundDialog(false);
      // Optionnel : rafraîchir les données de la commande
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Erreur lors du remboursement",
        severity: 'error'
      });
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography>Détails de la commande {order?.number}</Typography>
            {order?.order_status === 'pending' && (
              <>
                {order?.payment?.idTransaction ? (
                  <Button
                    variant="outlined"
                    startIcon={verifying ? <CircularProgress size={20} /> : <RefreshIcon />}
                    onClick={handleVerifyPayment}
                    disabled={verifying}
                    sx={{ ml: 2 }}
                  >
                    Revérifier paiement
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={transactionId}
                      onChange={(e) => setTransactionId(e.target.value)}
                      placeholder="Entrez l'ID de transaction"
                      style={{ marginRight: '8px' }}
                    />
                    <Select
                      value={paymentPartner}
                      onChange={(e) => setPaymentPartner(e.target.value)}
                      sx={{ marginRight: '8px' }}
                    >
                      <MenuItem value="FEDAPAY">FEDAPAY</MenuItem>
                      <MenuItem value="KKIAPAY">KKIAPAY</MenuItem>
                    </Select>
                    <Button
                      variant="outlined"
                      onClick={handleVerifyPayment}
                      disabled={verifying}
                      startIcon={<CheckIcon />}
                    >
                      Vérifier
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {verificationResult && (
            <Alert 
              severity={verificationResult.success ? "success" : "error"}
              sx={{ mb: 2 }}
            >
              {verificationResult.message}
            </Alert>
          )}
          
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Informations client</Typography>
                <Typography variant="body1">
                  Nom: {order?.client?.firstName} {order?.client?.lastName}
                </Typography>
                <Typography variant="body1">Email: {order?.client?.email}</Typography>
                <Typography variant="body1">Téléphone: {order?.client?.phone}</Typography>
                <Typography variant="body1">Pays: {order?.client?.country}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Informations commande</Typography>
                <Typography variant="body1">
                  Id: {order?.id}
                </Typography>
                <Typography variant="body1">
                  Montant total: {Number(order?.amount).toLocaleString()} FCFA
                </Typography>
                <Typography variant="body1">
                  Date: {new Date(order?.createdAt).toLocaleString()}
                </Typography>
                <Typography variant="body1">
                  Statut: <Chip 
                    label={order?.order_status} 
                    color={order?.order_status === 'success' ? 'success' : 'error'}
                    size="small"
                  />
                </Typography>
                <Typography variant="body1">
                  Statut retrait: <Chip 
                    label={order?.status_withdraw} 
                    color={order?.status_withdraw === 'success' ? 'success' : 'warning'}
                    size="small"
                  />
                </Typography>
                <Typography variant="body1">
                  Date de retrait: <Chip 
                    label={order?.date_withdraw?.date} 
                    size="small"
                  />
                </Typography>
              </Grid>

              {order?.payment && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Paiement</Typography>
                  <Typography variant="body1">
                    Méthode: {order?.payment?.paymentMethod} ({order?.payment?.operator})
                  </Typography>
                  <Typography variant="body1">
                    Transaction ID: {order?.payment?.idTransaction}
                  </Typography>
                  <Typography variant="body1">
                    Montant: {Number(order?.payment?.montant).toLocaleString()} {order?.payment?.currency}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Articles commandés</Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Produit</TableCell>
                        <TableCell align="right">Quantité</TableCell>
                        <TableCell align="right">Prix unitaire</TableCell>
                        <TableCell align="right">Montant</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order?.orderItems?.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.type}</TableCell>
                          <TableCell>{item.name}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">{Number(item.price).toLocaleString()} FCFA</TableCell>
                          <TableCell align="right">{Number(item.amount).toLocaleString()} FCFA</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              {order?.orderedTickets && order.orderedTickets.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Tickets</Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Nom</TableCell>
                          <TableCell>Activités</TableCell>
                          <TableCell>Statut</TableCell>
                          <TableCell>Numéro</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {order.orderedTickets.map((ticket, index) => (
                          <TableRow key={index}>
                            <TableCell>{ticket.name}</TableCell>
                            <TableCell>{ticket.activities.join(', ')}</TableCell>
                            <TableCell>
                              <Chip 
                                label={ticket.statut}
                                color={ticket.statut === 'valid' ? 'success' : 'warning'}
                                size="small"
                              />
                              {ticket.dateVerif && (
                                <Typography variant="caption" display="block">
                                  {new Date(ticket.dateVerif).toLocaleString()}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{ticket.number}</TableCell>
                            <TableCell>
                              <Button 
                                variant="outlined" 
                                size="small"
                                href={ticket.url}
                                target="_blank"
                              >
                                Voir le ticket
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}

              {order?.invoice && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Facture</Typography>
                  <Button 
                    variant="contained" 
                    href={order?.invoice?.url}
                    target="_blank"
                  >
                    Télécharger la facture
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
          {order?.order_status === 'success' && (
            <>
              {order?.invoice?.refund_url ? (
                <Button
                  variant="contained"
                  href={order.invoice.refund_url}
                  target="_blank"
                  sx={{ mt: 2 }}
                >
                  Facture de remboursement
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  onClick={() => setShowRefundDialog(true)}
                  sx={{ mt: 2 }}
                >
                  Procéder au remboursement
                </Button>
              )}
            </>
          )}
          {showRefundDialog && (
            <Dialog open={showRefundDialog} onClose={() => setShowRefundDialog(false)}>
              <DialogTitle>Sélectionner les articles à rembourser</DialogTitle>
              <DialogContent>
                {order?.orderItems?.map((item, index) => (
                  <div key={index}>
                    <Checkbox
                      checked={refundItems.includes(item.id)}
                      onChange={() => {
                        setRefundItems((prev) => 
                          prev.includes(item.id) ? prev.filter(id => id !== item.id) : [...prev, item.id]
                        );
                      }}
                    />
                    {item.name}
                  </div>
                ))}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowRefundDialog(false)}>Annuler</Button>
                <Button onClick={handleRefund}>Confirmer le remboursement</Button>
              </DialogActions>
            </Dialog>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default OrderDetails; 